import React from 'react';
import { Box, Divider, useTheme } from '@mui/material';
import Button from '../button/button';
import PropTypes from 'prop-types';

/**
 * `ButtonGroup` is a responsive component that renders a group of buttons.
 * It adjusts its layout depending on the screen size, displaying buttons in a column on smaller screens
 * and in a row with dividers on larger screens.
 *
 * @param {Object[]} buttons - An array of button objects, each containing the button's name and an optional onClick handler.
 * @param {string} buttons[].label - The display label of the button.
 * @param {('row'|'column')} [direction='column'] - The direction of the button group layout on large screens. Defaults to 'column'.
 * @param {number} [spacing=2] - The spacing between buttons on large screens. Defaults to 2.
 */
export default function ButtonGroup({
  buttons,
  direction = 'column',
  spacing = 2,
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: direction },
        gap: "20px",
        alignItems: { xs: 'flex-start', lg: 'center' },
        paddingTop: { xs: '46px', lg: '0' }, // Apply paddingTop only for xs
        backgroundColor: theme.palette.primary.main
      }}
    >
      {buttons.map((button, index) => (
        <React.Fragment key={index}>
          <Button
            variant="text"
            color="#68D3E3"
            fontFamily="body.fontFamily.secondary"
            fontWeight={600}
            sx={{
              padding: 0,
              justifyContent: 'flex-start',
              display: { xs: 'flex', lg: 'block' },
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              position: 'relative',
              mb: { xs: 0, lg: 0 },
              '&:hover': {
                textDecoration: 'underline',
                textDecorationColor: '#68D3E3',
                boxShadow: 'none',
                border: 'none',
                outline: 'none',
                background: 'none',
              },
            }}
            href={button.url}
          >
            {button.label}
          </Button>
          {index < buttons.length - 1 && (
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: 'none', lg: 'block' },
                height: '22px',
                mt: 0.5,
              }}
              className='footer-buttons-divider'
            />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}
ButtonGroup.propTypes = {
  /**
   * An array of objects representing the buttons to display.
   *
   * Each object should contain:
   * - `label`: The text label of the button (required).
   */
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  /**
   * The direction in which the buttons should be laid out on large screens.
   *
   * Can be either 'row' or 'column'. Defaults to 'column'.
   */
  direction: PropTypes.oneOf(['row', 'column']),
  /**
   * The spacing between buttons on large screens.
   *
   * This is a number representing the gap size, with a default value of 2.
   */
  spacing: PropTypes.number,
};
